import { tFunc } from './context/TranslationContext';
import {
  AvailableStore,
  CONSOLIDATED_STORE_ID,
  NO_STORE_ID,
  REMOTE_LOCATION_POSTAL_CODE,
} from './services/Appointment/model';

export type MS = number;

export type ServiceLocation =
  | 'inStore'
  | 'home'
  | 'storeRemote'
  | 'remote'
  | 'storeHome';

export const getLocationType = (
  locationType: ServiceLocation,
): ServiceLocation => {
  if (['remote', 'storeRemote'].includes(locationType)) {
    return 'remote';
  }
  if (['home', 'storeHome'].includes(locationType)) {
    return 'home';
  }
  return 'inStore';
};

export function asyncWait(timeToWait: MS): Promise<void> {
  return new Promise((resolve): void => {
    setTimeout((): void => {
      resolve();
    }, timeToWait);
  });
}

export const isConsolidatedCalendarEnabled = (
  currentService: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  const { consolidatedCalendar } = appConfig.SERVICES;
  return (
    consolidatedCalendar.enabled &&
    !consolidatedCalendar.excludedServices.includes(currentService)
  );
};

export function removeTimePartFromDate(date: string): string {
  const [datePart] = date.split('T');
  return datePart;
}

export const isServiceLocationStore = (location: ServiceLocation): boolean => {
  return location === 'inStore';
};

export const isServiceProvidedRemote = (location: ServiceLocation): boolean => {
  return location === 'remote' || location === 'storeRemote';
};

export const isServiceProvidedAtHome = (location: ServiceLocation): boolean => {
  return location === 'home' || location === 'storeHome';
};

export const isTimeInBrowserSettings = (
  location: ServiceLocation,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  return (
    appConfig.MULTIPLETIMEZONES &&
    (location === 'home' || location === 'remote' || location === 'storeHome')
  );
};

export const isStoreRemoteInConsolidatedCalendar = (
  location: ServiceLocation,
  currentService: string,
  appConfig: typeof CSS_CONFIG,
): boolean => {
  return (
    isConsolidatedCalendarEnabled(currentService, appConfig) &&
    location === 'storeRemote'
  );
};

export const isDistanceServiceProvidedByStore = (
  location: ServiceLocation,
): boolean => {
  return location === 'storeRemote' || location === 'storeHome';
};

export const getServiceLocationKey = (location: ServiceLocation): string => {
  if (isServiceProvidedAtHome(location)) {
    return 'home';
  }
  return location;
};

export const isStoreService = (location: ServiceLocation): boolean => {
  switch (location) {
    case 'inStore':
    case 'storeRemote':
    case 'storeHome':
      return true;
    case 'home':
    default:
      return false;
  }
};

export const isOutOfStoreService = (location: ServiceLocation): boolean => {
  return location === 'home' || location === 'remote';
};

export const startOfWeekDayToNumber = (day: string): 0 | 1 => {
  if (day.toUpperCase() === 'SUNDAY') {
    return 0;
  }
  return 1;
};

export const getReplacementServiceTitle = (
  t: tFunc,
  replacementServiceTitle: string,
  location?: ServiceLocation,
): string => {
  const locationLabel = location
    ? t(`components.servicePicker.${getServiceLocationKey(location)}`)
    : '';
  return `${replacementServiceTitle}${locationLabel ? ` - ${locationLabel}` : ''}`;
};

export const getDefaultReplacementStoreId = (
  stores: AvailableStore[],
  serviceId: string,
  serviceLocation: ServiceLocation,
  appConfig: typeof CSS_CONFIG,
): string => {
  const isConsolidatedCalendarUsed = isStoreRemoteInConsolidatedCalendar(
    serviceLocation,
    serviceId,
    appConfig,
  );

  if (isConsolidatedCalendarUsed) {
    const { defaultStorePostalCode } = appConfig.SERVICES.consolidatedCalendar;
    const defaultStore = stores.find(
      (store) => store.zipCode === defaultStorePostalCode,
    );
    return defaultStore ? defaultStore.value : CONSOLIDATED_STORE_ID;
  }

  return stores[0]?.value;
};

export const getReplacementServiceStoreId = (
  originalStoreId: string,
  originalLocation: ServiceLocation,
  replacementServiceId: string,
  replacementLocation: ServiceLocation,
  replacementStores: AvailableStore[],
  appConfig: typeof CSS_CONFIG,
): string | undefined => {
  if (!isStoreService(replacementLocation)) {
    // case of remote or home replacement service, return NO_STORE_ID
    return NO_STORE_ID;
  }

  // case of remote or home original service, return default replacement store
  if (originalStoreId === NO_STORE_ID) {
    return getDefaultReplacementStoreId(
      replacementStores,
      replacementServiceId,
      replacementLocation,
      appConfig,
    );
  }

  // case of inStore original and replacement services, return the same store if available
  if (
    isServiceLocationStore(originalLocation) &&
    isServiceLocationStore(replacementLocation)
  ) {
    return replacementStores.find((store) => store.value === originalStoreId)
      ?.value;
  }

  // the following cases are when there is a mix of inStore and storeRemote/storeHome
  // or storeRemote and storeHome between original and replacement services
  const isOriginalStoreIdConsolidated =
    originalStoreId === CONSOLIDATED_STORE_ID;
  const shouldReplacementStoreUseConsolidatedId =
    isStoreRemoteInConsolidatedCalendar(
      replacementLocation,
      replacementServiceId,
      appConfig,
    );
  // return consolidated store id if both original and replacement services are
  // using consolidated calendar and it is selected for the original service.
  if (
    isOriginalStoreIdConsolidated &&
    shouldReplacementStoreUseConsolidatedId
  ) {
    return CONSOLIDATED_STORE_ID;
  }

  const matchingStoreId = replacementStores.find(
    (store) => store.value === originalStoreId,
  )?.value;
  // return the same store if available in replacement stores, otherwise return
  // the default store for replacement service
  return (
    matchingStoreId ||
    getDefaultReplacementStoreId(
      replacementStores,
      replacementServiceId,
      replacementLocation,
      appConfig,
    )
  );
};

export const getPostalCodeForLocation = (
  storeId: string,
  stores: AvailableStore[],
  appConfig: typeof CSS_CONFIG,
  location?: ServiceLocation,
): string => {
  if (location && !isStoreService(location)) return REMOTE_LOCATION_POSTAL_CODE;
  if (storeId === CONSOLIDATED_STORE_ID)
    return appConfig.SERVICES.consolidatedCalendar.consolidatedPostalCode;
  return stores.find((store) => store.value === storeId)?.zipCode || '';
};
